// Mock data for the "POG On-Time Projection" IFP report (both tabs:
// "POG On-Time Projection" hierarchical matrix and "POG Details" flat view).
//
// Anchor data (Set Dates 10/30/2026 + 10/23/2026, their POG Groups, Events,
// and every visible week-column percentage on the projection tab; the ~13
// detail rows on the details tab) is transcribed verbatim from the reference
// screenshots. Percentages are NOT stored as bare numbers and averaged —
// each anchor Event carries a real `openOrderSkuCount` (denominator, fixed
// per event) and a per-week `onTimeSkuCount` (numerator, grows over the
// projection horizon). Every roll-up (POG Group Total, Set Date Total) is a
// genuine `sum(onTime) / sum(openOrder)` over its descendants, verified to
// reproduce the screenshot's own numbers exactly (e.g. "Front End: CMAS
// Glow / Total" = 67% from two children at 100% and 0% only works with a
// ~2:1 SKU-count weighting — a plain average would give 50%).
//
// One exception: the grand "Total" row. Both visible Set Dates' own
// sub-percentages are internally *constant* across the -2wk..+4wk columns
// (10/30/2026 stays at 79% across that whole span, 10/23/2026 stays at
// 100%), so any real weighted combination of just those two would also be
// constant across that span — but the screenshot's grand Total instead
// climbs smoothly (84, 87, 88, 92, 94). That can only mean the real report's
// grand Total also folds in additional Set Dates that aren't visible in the
// screenshot (the table has a scrollbar). Rather than inventing fake hidden
// rows just to force the arithmetic to line up, the grand Total is anchored
// as an explicit override — the same documented-quirk approach already used
// for the PO Level Detail View's static "0" Days Shifted total. It only
// applies when every filter is at its default ("All") state; once any
// filter narrows the data, the grand Total is computed for real from
// whatever Set Date groups are currently visible.
//
// The color fill on "By set" is a simple, documented threshold rule (>=90
// green, 70-89 yellow, <70 red) plus exactly one hardcoded override: the
// grand Total's 84% is red in the screenshot despite falling in the
// yellow band. (The plan that preceded this file also flagged "Front End:
// CMAS Glow / Total" at 67% as needing an override — it doesn't: 67 is
// already < 70, so the default threshold rule already colors it red,
// matching the screenshot with no override needed.)

export type CoverageColor = 'green' | 'yellow' | 'red'

export const STATUS_CELL_CLASSES: Record<CoverageColor, string> = {
  green: 'bg-green-100',
  yellow: 'bg-amber-100',
  red: 'bg-rose-100',
}

export interface WeekColumnDef {
  key: WeekKey
  label: string
}

export type WeekKey =
  | 'wkMinus2'
  | 'wkMinus1'
  | 'bySet'
  | 'wkPlus1'
  | 'wkPlus2'
  | 'wkPlus3'
  | 'wkPlus4'
  | 'wkPlus8'
  | 'wkPlus12'
  | 'wkPlus16'

export const WEEK_COLUMNS: WeekColumnDef[] = [
  { key: 'wkMinus2', label: '-2 wk' },
  { key: 'wkMinus1', label: '-1 wk' },
  { key: 'bySet', label: 'By set' },
  { key: 'wkPlus1', label: '+1 wk' },
  { key: 'wkPlus2', label: '+2 wks' },
  { key: 'wkPlus3', label: '+3 wks' },
  { key: 'wkPlus4', label: '+4 wks' },
  { key: 'wkPlus8', label: '+8 wks' },
  { key: 'wkPlus12', label: '+12 wks' },
  { key: 'wkPlus16', label: '+16 wks' },
]

const WEEK_KEYS: WeekKey[] = WEEK_COLUMNS.map(c => c.key)

function hashString(input: string): number {
  let h = 2166136261
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function normalizedFromHash(seed: number, salt: string): number {
  return (hashString(`${seed}:${salt}`) % 10000) / 10000
}

export const DC_OPTIONS = [
  '1_Dallas',
  '2_Atlanta',
  '3_Memphis',
  '4_Indianapolis',
  '5_Los Angeles',
]

export const SEASONAL_CORE_OPTIONS = ['Seasonal', 'Core']
export const POG_SET_TYPE_OPTIONS = [
  'New Store',
  'Full Reset',
  'Update',
  'Cut-In',
]
// Reused verbatim from EventProductFlowMockup.tsx / eventProductFlowMockData.ts
// (neither exports it, so it's duplicated here rather than imported).
export const INITIAL_REPLEN_OPTIONS = ['Initial', 'Replen', 'Reset']

// ---------------------------------------------------------------------------
// Counts model
// ---------------------------------------------------------------------------

export interface WeekCounts {
  openOrder: number
  onTime: number
}

export interface DcLeaf {
  dc: string
  counts: Record<WeekKey, WeekCounts>
}

export interface EventNode {
  id: string
  event: string
  seasonalCore: string
  pogSetType: string
  initialReplen: string
  dcLeaves: DcLeaf[]
}

export interface PogGroupNode {
  id: string
  pogGroup: string
  events: EventNode[]
}

export interface SetDateNode {
  id: string
  setDateLabel: string
  setDate: Date
  pogGroups: PogGroupNode[]
}

function pctsToOnTimeCounts(total: number, pcts: number[]): number[] {
  return pcts.map(p => Math.round((total * p) / 100))
}

function makeCounts(
  openOrder: number,
  pcts: number[]
): Record<WeekKey, WeekCounts> {
  const onTimes = pctsToOnTimeCounts(openOrder, pcts)
  const record = {} as Record<WeekKey, WeekCounts>
  WEEK_KEYS.forEach((key, i) => {
    record[key] = { openOrder, onTime: onTimes[i] }
  })
  return record
}

// Parses a zero-padded "MM/DD/YYYY" label into a Date (local time).
function parseMonthDayYear(label: string): Date {
  const [month, day, year] = label.split('/').map(Number)
  return new Date(year, month - 1, day)
}

// Parses a "YYYY-MM-DD" string into a local-time Date — used instead of
// `new Date(isoString)` (which parses as UTC midnight) so every date in
// this file compares consistently regardless of the viewer's timezone.
function ymd(iso: string): Date {
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(year, month - 1, day)
}

function leaf(dc: string, openOrder: number, pcts: number[]): DcLeaf {
  return { dc, counts: makeCounts(openOrder, pcts) }
}

// --- Anchors — transcribed from "POG On-Time Projection" screenshot ---

const ANCHOR_SET_DATES: SetDateNode[] = [
  {
    id: 'sd-2026-10-30',
    setDateLabel: '10/30/2026',
    setDate: ymd('2026-10-30'),
    pogGroups: [
      {
        id: 'pg-cmas-glow',
        pogGroup: 'POG-1001',
        events: [
          {
            id: 'ev-sea38-cmas',
            event: 'SEA38',
            seasonalCore: 'Seasonal',
            pogSetType: 'Full Reset',
            initialReplen: 'Initial',
            dcLeaves: [
              leaf(
                '5_Los Angeles',
                366,
                [0, 100, 100, 100, 100, 100, 100, 100, 100, 100]
              ),
            ],
          },
          {
            id: 'ev-sea44-cmas',
            event: 'SEA44',
            seasonalCore: 'Seasonal',
            pogSetType: 'Full Reset',
            initialReplen: 'Initial',
            dcLeaves: [
              leaf('5_Los Angeles', 183, [0, 0, 0, 0, 0, 0, 0, 100, 100, 100]),
            ],
          },
        ],
      },
      {
        id: 'pg-register1',
        pogGroup: 'POG-1002',
        events: [
          {
            id: 'ev-sea37-reg1',
            event: 'SEA37',
            seasonalCore: 'Seasonal',
            pogSetType: 'New Store',
            initialReplen: 'Replen',
            dcLeaves: [
              leaf(
                '1_Dallas',
                100,
                [100, 100, 100, 100, 100, 100, 100, 100, 100, 100]
              ),
            ],
          },
          {
            id: 'ev-sea39-reg1',
            event: 'SEA39',
            seasonalCore: 'Seasonal',
            pogSetType: 'New Store',
            initialReplen: 'Replen',
            dcLeaves: [
              leaf(
                '1_Dallas',
                99,
                [0, 0, 100, 100, 100, 100, 100, 100, 100, 100]
              ),
            ],
          },
          {
            id: 'ev-sea42-reg1',
            event: 'SEA42',
            seasonalCore: 'Seasonal',
            pogSetType: 'New Store',
            initialReplen: 'Replen',
            dcLeaves: [
              leaf('2_Atlanta', 1, [0, 0, 0, 0, 0, 100, 100, 100, 100, 100]),
            ],
          },
        ],
      },
      {
        id: 'pg-rico',
        pogGroup: 'POG-1003',
        events: [
          {
            id: 'ev-sba39-rico',
            event: 'SBA39',
            seasonalCore: 'Core',
            pogSetType: 'Update',
            initialReplen: 'Reset',
            dcLeaves: [
              leaf(
                '3_Memphis',
                121,
                [0, 0, 100, 100, 100, 100, 100, 100, 100, 100]
              ),
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'sd-2026-10-23',
    setDateLabel: '10/23/2026',
    setDate: ymd('2026-10-23'),
    pogGroups: [
      {
        id: 'pg-b2c-holiday',
        pogGroup: 'POG-1004',
        events: [
          {
            id: 'ev-sea38-b2c',
            event: 'SEA38',
            seasonalCore: 'Seasonal',
            pogSetType: 'Cut-In',
            initialReplen: 'Initial',
            dcLeaves: [
              leaf(
                '4_Indianapolis',
                1,
                [0, 0, 100, 100, 100, 100, 100, 100, 100, 100]
              ),
            ],
          },
        ],
      },
      {
        id: 'pg-doormats',
        pogGroup: 'POG-1005',
        events: [
          {
            id: 'ev-sea38-doormats',
            event: 'SEA38',
            seasonalCore: 'Seasonal',
            pogSetType: 'Cut-In',
            initialReplen: 'Initial',
            dcLeaves: [
              leaf(
                '5_Los Angeles',
                1,
                [0, 0, 100, 100, 100, 100, 100, 100, 100, 100]
              ),
            ],
          },
        ],
      },
    ],
  },
]

// The grand Total row exactly as shown in the screenshot — see the header
// comment for why this can't be reproduced by real aggregation of only the
// two visible Set Dates.
const ANCHOR_GRAND_TOTAL_PCTS: Record<WeekKey, number> = {
  wkMinus2: 2,
  wkMinus1: 49,
  bySet: 84,
  wkPlus1: 87,
  wkPlus2: 88,
  wkPlus3: 92,
  wkPlus4: 94,
  wkPlus8: 100,
  wkPlus12: 100,
  wkPlus16: 100,
}

// --- Filler Set Dates — synthetic, sorted earlier than 10/23/2026 so they
// always render *below* the two anchors (matching the PO Level Detail
// View precedent of filler ranking below anchors rather than needing to be
// invisible). Gives every filter (DC/Event/POG Group/Seasonal-Core/POG Set
// Type/Initial-Replen) a non-trivial option list and non-empty results.

const FILLER_POG_GROUP_NAMES = [
  'POG-2001',
  'POG-2002',
  'POG-2003',
  'POG-2004',
  'POG-2005',
  'POG-2006',
  'POG-2007',
  'POG-2008',
  'POG-2009',
  'POG-2010',
]

const FILLER_SET_DATE_LABELS = [
  '09/25/2026',
  '09/18/2026',
  '09/11/2026',
  '08/28/2026',
  '08/14/2026',
  '07/31/2026',
  '07/17/2026',
  '07/03/2026',
  '06/19/2026',
  '06/05/2026',
]

function coverageCurve(seed: number): number[] {
  // Ramps from a hash-derived starting point up to full coverage over the
  // projection horizon, mirroring the shape of the anchor events.
  const rampStart = Math.round(normalizedFromHash(seed, 'rampStart') * 40) // 0-40
  const rampCol = 1 + Math.floor(normalizedFromHash(seed, 'rampCol') * 4) // col index 1-4 where it jumps to 100
  return WEEK_KEYS.map((_, i) => (i >= rampCol ? 100 : rampStart))
}

function buildFillerSetDate(index: number): SetDateNode {
  const id = `sd-filler-${index}`
  const seed = hashString(id)
  const groupCount = 1 + Math.floor(normalizedFromHash(seed, 'groupCount') * 2) // 1-2

  const pogGroups: PogGroupNode[] = Array.from(
    { length: groupCount },
    (_, gi) => {
      const groupSeed = hashString(`${id}-group-${gi}`)
      const groupName =
        FILLER_POG_GROUP_NAMES[
          Math.floor(
            normalizedFromHash(groupSeed, 'name') *
              FILLER_POG_GROUP_NAMES.length
          )
        ]
      const eventCount =
        1 + Math.floor(normalizedFromHash(groupSeed, 'eventCount') * 2) // 1-2

      const events: EventNode[] = Array.from(
        { length: eventCount },
        (_, ei) => {
          const eventSeed = hashString(`${id}-group-${gi}-event-${ei}`)
          const eventPrefix =
            normalizedFromHash(eventSeed, 'prefix') < 0.5 ? 'SEA' : 'SBA'
          const eventNumber =
            1 + Math.floor(normalizedFromHash(eventSeed, 'num') * 46)
          const dc =
            DC_OPTIONS[
              Math.floor(
                normalizedFromHash(eventSeed, 'dc') * DC_OPTIONS.length
              )
            ]
          const seasonalCore =
            SEASONAL_CORE_OPTIONS[
              Math.floor(
                normalizedFromHash(eventSeed, 'sc') *
                  SEASONAL_CORE_OPTIONS.length
              )
            ]
          const pogSetType =
            POG_SET_TYPE_OPTIONS[
              Math.floor(
                normalizedFromHash(eventSeed, 'pst') *
                  POG_SET_TYPE_OPTIONS.length
              )
            ]
          const initialReplen =
            INITIAL_REPLEN_OPTIONS[
              Math.floor(
                normalizedFromHash(eventSeed, 'ir') *
                  INITIAL_REPLEN_OPTIONS.length
              )
            ]
          const openOrder =
            5 + Math.floor(normalizedFromHash(eventSeed, 'weight') * 45) // 5-50
          const pcts = coverageCurve(eventSeed)

          return {
            id: `ev-filler-${eventSeed}`,
            event: `${eventPrefix}${String(eventNumber).padStart(2, '0')}`,
            seasonalCore,
            pogSetType,
            initialReplen,
            dcLeaves: [leaf(dc, openOrder, pcts)],
          }
        }
      )

      return {
        id: `pg-filler-${groupSeed}`,
        pogGroup: groupName,
        events,
      }
    }
  )

  return {
    id,
    setDateLabel: FILLER_SET_DATE_LABELS[index],
    setDate: parseMonthDayYear(FILLER_SET_DATE_LABELS[index]),
    pogGroups,
  }
}

const FILLER_SET_DATES: SetDateNode[] = FILLER_SET_DATE_LABELS.map((_, i) =>
  buildFillerSetDate(i)
)

export const ALL_SET_DATES: SetDateNode[] = [
  ...ANCHOR_SET_DATES,
  ...FILLER_SET_DATES,
]

export const EVENT_OPTIONS: string[] = Array.from(
  new Set(
    ALL_SET_DATES.flatMap(sd =>
      sd.pogGroups.flatMap(pg => pg.events.map(e => e.event))
    )
  )
).sort()

export const POG_GROUP_OPTIONS: string[] = Array.from(
  new Set(ALL_SET_DATES.flatMap(sd => sd.pogGroups.map(pg => pg.pogGroup)))
).sort()

export const SET_DATE_OPTIONS: string[] = Array.from(
  new Set(ALL_SET_DATES.map(sd => sd.setDateLabel))
).sort()

// ---------------------------------------------------------------------------
// Snapshot date options — zero-padded MM/DD/YYYY to match the screenshot
// exactly. Anchored on 2026-07-01 (the exact date shown in both reference
// screenshots), stepping back weekly so the option list stays stable.
// ---------------------------------------------------------------------------

const BASE_SNAPSHOT_DATE = '07/01/2026'

function formatSnapshotDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${month}/${day}/${date.getFullYear()}`
}

const RECENT_SNAPSHOT_DATES: string[] = (() => {
  const dates: string[] = []
  let cursor = ymd('2026-07-01')
  for (let i = 0; i < 5; i++) {
    dates.push(formatSnapshotDate(cursor))
    cursor = new Date(cursor)
    cursor.setDate(cursor.getDate() - 7)
  }
  return dates
})()

export const SNAPSHOT_DATE_OPTIONS = ['Latest', ...RECENT_SNAPSHOT_DATES]

// ---------------------------------------------------------------------------
// Shared filter state (hoisted across both tabs)
// ---------------------------------------------------------------------------

export interface PogFilterState {
  snapshotDate: string
  departmentId: string | null
  classId: string | null
  subclassId: string | null
  dc: string | null
  pogSetDateStart: string | null
  pogSetDateEnd: string | null
  seasonalCore: string | null
  seasonId: string | null
  event: string | null
  pogGroup: string | null
  pogSetType: string | null
  initialReplen: string | null
  // Details tab only, but hoisted so they persist across tab switches.
  po: string | null
  sku: string | null
  vendor: string | null
}

function shrinkForFilter(name: string, value: string): number {
  const norm = normalizedFromHash(hashString(`${name}:${value}`), 'shrink')
  return 0.4 + norm * 0.35 // [0.4, 0.75]
}

function cosmeticFilterPairs(filters: PogFilterState): Array<[string, string]> {
  const pairs: Array<[string, string]> = []
  if (filters.departmentId) pairs.push(['department', filters.departmentId])
  if (filters.classId) pairs.push(['class', filters.classId])
  if (filters.subclassId) pairs.push(['subclass', filters.subclassId])
  if (filters.seasonId) pairs.push(['season', filters.seasonId])
  if (
    filters.snapshotDate !== 'Latest' &&
    filters.snapshotDate !== BASE_SNAPSHOT_DATE
  ) {
    pairs.push(['snapshotDate', filters.snapshotDate])
  }
  return pairs
}

function hasAnyExactMatchFilter(filters: PogFilterState): boolean {
  return Boolean(
    filters.dc ||
    filters.pogSetDateStart ||
    filters.pogSetDateEnd ||
    filters.event ||
    filters.pogGroup ||
    filters.seasonalCore ||
    filters.pogSetType ||
    filters.initialReplen
  )
}

function isDefaultState(filters: PogFilterState): boolean {
  return (
    !hasAnyExactMatchFilter(filters) &&
    cosmeticFilterPairs(filters).length === 0
  )
}

// The "POG Details" footer's "Total" Days Diff (266, from the reference
// screenshot) can't be reproduced as a real sum of the 13 transcribed anchor
// rows (they only sum to 28) — same documented-quirk situation as the
// projection tab's grand Total: the reference table has a scrollbar, so its
// footer aggregates more underlying rows than are visible in the screenshot.
// This anchor is used verbatim only when every filter (including the
// Details-only PO/SKU/Vendor pickers) is at its default "All" state; once
// any filter narrows the data, the footer is computed for real from
// whatever rows are currently visible.
export const ANCHOR_DETAIL_TOTAL_DAYS_DIFF = 266

export function isDefaultDetailFilterState(filters: PogFilterState): boolean {
  return (
    isDefaultState(filters) && !filters.po && !filters.sku && !filters.vendor
  )
}

function computeColor(pct: number): CoverageColor {
  if (pct >= 90) return 'green'
  if (pct >= 70) return 'yellow'
  return 'red'
}

function clampPct(pct: number): number {
  return Math.min(100, Math.max(0, pct))
}

// ---------------------------------------------------------------------------
// Display tree — filtered, shrunk/wiggled, and aggregated bottom-up
// ---------------------------------------------------------------------------

export interface DisplayCell {
  pct: number
  color: CoverageColor
}

export interface DisplayDcRow {
  dc: string
  cells: Record<WeekKey, DisplayCell>
}

export interface DisplayEventRow {
  id: string
  event: string
  cells: Record<WeekKey, DisplayCell>
  dcRows: DisplayDcRow[]
}

export interface DisplayGroupRow {
  id: string
  pogGroup: string
  cells: Record<WeekKey, DisplayCell>
  events: DisplayEventRow[]
}

export interface DisplaySetDateRow {
  id: string
  setDateLabel: string
  cells: Record<WeekKey, DisplayCell>
  groups: DisplayGroupRow[]
}

export interface ProjectionTableData {
  grand: Record<WeekKey, DisplayCell>
  setDates: DisplaySetDateRow[]
}

function aggregateCounts(
  counts: Array<Record<WeekKey, WeekCounts>>
): Record<WeekKey, WeekCounts> {
  const out = {} as Record<WeekKey, WeekCounts>
  WEEK_KEYS.forEach(key => {
    out[key] = counts.reduce(
      (acc, c) => ({
        openOrder: acc.openOrder + c[key].openOrder,
        onTime: acc.onTime + c[key].onTime,
      }),
      { openOrder: 0, onTime: 0 }
    )
  })
  return out
}

function cellsFromCounts(
  counts: Record<WeekKey, WeekCounts>,
  wiggleSeed: number,
  wiggleKey: string,
  colorOverride?: Partial<Record<WeekKey, CoverageColor>>
): Record<WeekKey, DisplayCell> {
  const cells = {} as Record<WeekKey, DisplayCell>
  WEEK_KEYS.forEach(key => {
    const { openOrder, onTime } = counts[key]
    const rawPct = openOrder > 0 ? Math.round((onTime / openOrder) * 100) : 0
    const noise = wiggleSeed
      ? Math.round(
          (normalizedFromHash(wiggleSeed, `${wiggleKey}:${key}`) - 0.5) * 10
        ) // +-5pts
      : 0
    const pct = clampPct(rawPct + noise)
    const override = colorOverride?.[key]
    cells[key] = { pct, color: override ?? computeColor(pct) }
  })
  return cells
}

export function buildProjectionTableData(
  filters: PogFilterState
): ProjectionTableData {
  const cosmeticPairs = cosmeticFilterPairs(filters)
  const shrink = cosmeticPairs.reduce(
    (acc, [name, value]) => acc * shrinkForFilter(name, value),
    1
  )
  const wiggleSeed = cosmeticPairs.length
    ? hashString(cosmeticPairs.map(([n, v]) => `${n}=${v}`).join('|'))
    : 0
  const defaultState = isDefaultState(filters)

  const rangeStart = filters.pogSetDateStart
    ? ymd(filters.pogSetDateStart)
    : null
  const rangeEnd = filters.pogSetDateEnd ? ymd(filters.pogSetDateEnd) : null

  interface WithCounts<T> {
    display: T
    counts: Record<WeekKey, WeekCounts>
  }

  const setDateResults: Array<WithCounts<DisplaySetDateRow>> =
    ALL_SET_DATES.filter(sd => {
      if (rangeStart && sd.setDate < rangeStart) return false
      if (rangeEnd && sd.setDate > rangeEnd) return false
      return true
    })
      .map(sd => {
        const groupResults: Array<WithCounts<DisplayGroupRow>> = sd.pogGroups
          .filter(pg => !filters.pogGroup || pg.pogGroup === filters.pogGroup)
          .map(pg => {
            const eventResults: Array<WithCounts<DisplayEventRow>> = pg.events
              .filter(ev => !filters.event || ev.event === filters.event)
              .filter(
                ev =>
                  !filters.seasonalCore ||
                  ev.seasonalCore === filters.seasonalCore
              )
              .filter(
                ev =>
                  !filters.pogSetType || ev.pogSetType === filters.pogSetType
              )
              .filter(
                ev =>
                  !filters.initialReplen ||
                  ev.initialReplen === filters.initialReplen
              )
              .map(ev => {
                const dcLeaves = ev.dcLeaves.filter(
                  l => !filters.dc || l.dc === filters.dc
                )
                if (dcLeaves.length === 0) return null

                const shrunkLeaves = dcLeaves.map(l => {
                  const counts = {} as Record<WeekKey, WeekCounts>
                  WEEK_KEYS.forEach(key => {
                    const openOrder = Math.max(
                      1,
                      Math.round(l.counts[key].openOrder * shrink)
                    )
                    const onTime = Math.min(
                      openOrder,
                      Math.max(0, Math.round(l.counts[key].onTime * shrink))
                    )
                    counts[key] = { openOrder, onTime }
                  })
                  return { dc: l.dc, counts }
                })

                const dcRows: DisplayDcRow[] = shrunkLeaves.map(l => ({
                  dc: l.dc,
                  cells: cellsFromCounts(
                    l.counts,
                    wiggleSeed,
                    `${ev.id}-${l.dc}`
                  ),
                }))

                const eventCounts = aggregateCounts(
                  shrunkLeaves.map(l => l.counts)
                )
                const result: WithCounts<DisplayEventRow> = {
                  display: {
                    id: ev.id,
                    event: ev.event,
                    cells: cellsFromCounts(eventCounts, wiggleSeed, ev.id),
                    dcRows,
                  },
                  counts: eventCounts,
                }
                return result
              })
              .filter((e): e is WithCounts<DisplayEventRow> => e !== null)

            if (eventResults.length === 0) return null

            const groupCounts = aggregateCounts(eventResults.map(e => e.counts))
            const result: WithCounts<DisplayGroupRow> = {
              display: {
                id: pg.id,
                pogGroup: pg.pogGroup,
                cells: cellsFromCounts(groupCounts, wiggleSeed, pg.id),
                events: eventResults.map(e => e.display),
              },
              counts: groupCounts,
            }
            return result
          })
          .filter((g): g is WithCounts<DisplayGroupRow> => g !== null)

        if (groupResults.length === 0) return null

        const setDateCounts = aggregateCounts(groupResults.map(g => g.counts))
        const result: WithCounts<DisplaySetDateRow> = {
          display: {
            id: sd.id,
            setDateLabel: sd.setDateLabel,
            cells: cellsFromCounts(setDateCounts, wiggleSeed, sd.id),
            groups: groupResults.map(g => g.display),
          },
          counts: setDateCounts,
        }
        return result
      })
      .filter((sd): sd is WithCounts<DisplaySetDateRow> => sd !== null)
      .sort((a, b) =>
        a.display.setDateLabel < b.display.setDateLabel ? 1 : -1
      )

  const visibleSetDates = setDateResults.map(sd => sd.display)

  let grand: Record<WeekKey, DisplayCell>
  if (defaultState) {
    const cells = {} as Record<WeekKey, DisplayCell>
    WEEK_KEYS.forEach(key => {
      const pct = ANCHOR_GRAND_TOTAL_PCTS[key]
      cells[key] = {
        pct,
        color: key === 'bySet' ? 'red' : computeColor(pct),
      }
    })
    grand = cells
  } else if (setDateResults.length > 0) {
    const grandCounts = aggregateCounts(setDateResults.map(sd => sd.counts))
    grand = cellsFromCounts(grandCounts, wiggleSeed, 'grand')
  } else {
    grand = {} as Record<WeekKey, DisplayCell>
    WEEK_KEYS.forEach(key => {
      grand[key] = { pct: 0, color: 'red' }
    })
  }

  return {
    grand,
    setDates: visibleSetDates,
  }
}

// ---------------------------------------------------------------------------
// POG Details — flat detail rows
// ---------------------------------------------------------------------------

export interface PogDetailRow {
  id: string
  pogGroup: string
  event: string
  dc: string
  po: string
  vendor: string
  sku: string
  inStoreDate: Date
  setDate: Date
  seasonalCore: string
  pogSetType: string
  initialReplen: string
}

export function daysDiffFor(row: PogDetailRow): number {
  const ms = row.setDate.getTime() - row.inStoreDate.getTime()
  return Math.round(ms / (1000 * 60 * 60 * 24))
}

// Transcribed verbatim from the "POG Details" screenshot.
const ANCHOR_DETAIL_ROWS: PogDetailRow[] = [
  {
    id: 'pd-1',
    pogGroup: 'POG-3001',
    event: 'SEA21',
    dc: '5_Los Angeles',
    po: '40975669',
    vendor: 'BEAU MONDE HONG KONG COMPANY LIMITED',
    sku: '10810214',
    inStoreDate: ymd('2026-06-26'),
    setDate: ymd('2026-07-03'),
    seasonalCore: 'Seasonal',
    pogSetType: 'Full Reset',
    initialReplen: 'Initial',
  },
  {
    id: 'pd-2',
    pogGroup: 'POG-3001',
    event: 'SEA21',
    dc: '5_Los Angeles',
    po: '40975669',
    vendor: 'BEAU MONDE HONG KONG COMPANY LIMITED',
    sku: '10810215',
    inStoreDate: ymd('2026-06-26'),
    setDate: ymd('2026-07-03'),
    seasonalCore: 'Seasonal',
    pogSetType: 'Full Reset',
    initialReplen: 'Initial',
  },
  {
    id: 'pd-3',
    pogGroup: 'POG-3001',
    event: 'SEA21',
    dc: '5_Los Angeles',
    po: '40975669',
    vendor: 'BEAU MONDE HONG KONG COMPANY LIMITED',
    sku: '10810216',
    inStoreDate: ymd('2026-06-26'),
    setDate: ymd('2026-07-03'),
    seasonalCore: 'Seasonal',
    pogSetType: 'Full Reset',
    initialReplen: 'Initial',
  },
  {
    id: 'pd-4',
    pogGroup: 'POG-3002',
    event: 'SEA07',
    dc: '5_Los Angeles',
    po: '40660173',
    vendor: 'CASA COLLECTIVE LIMITED',
    sku: '10808418',
    inStoreDate: ymd('2026-03-27'),
    setDate: ymd('2026-03-27'),
    seasonalCore: 'Core',
    pogSetType: 'New Store',
    initialReplen: 'Initial',
  },
  {
    id: 'pd-5',
    pogGroup: 'POG-3002',
    event: 'SEA07',
    dc: '5_Los Angeles',
    po: '40660173',
    vendor: 'CASA COLLECTIVE LIMITED',
    sku: '10808677',
    inStoreDate: ymd('2026-03-27'),
    setDate: ymd('2026-03-27'),
    seasonalCore: 'Core',
    pogSetType: 'New Store',
    initialReplen: 'Initial',
  },
  {
    id: 'pd-6',
    pogGroup: 'POG-3002',
    event: 'SEA07',
    dc: '5_Los Angeles',
    po: '40660185',
    vendor: 'IN THE FLOW DESIGN LIMITED',
    sku: '10808183',
    inStoreDate: ymd('2026-03-27'),
    setDate: ymd('2026-03-27'),
    seasonalCore: 'Core',
    pogSetType: 'New Store',
    initialReplen: 'Initial',
  },
  {
    id: 'pd-7',
    pogGroup: 'POG-3002',
    event: 'SEA07',
    dc: '5_Los Angeles',
    po: '40660185',
    vendor: 'IN THE FLOW DESIGN LIMITED',
    sku: '10808190',
    inStoreDate: ymd('2026-03-20'),
    setDate: ymd('2026-03-27'),
    seasonalCore: 'Core',
    pogSetType: 'New Store',
    initialReplen: 'Initial',
  },
  {
    id: 'pd-8',
    pogGroup: 'POG-3002',
    event: 'SEA07',
    dc: '5_Los Angeles',
    po: '40660193',
    vendor: 'IN THE FLOW DESIGN LIMITED',
    sku: '10808179',
    inStoreDate: ymd('2026-03-27'),
    setDate: ymd('2026-03-27'),
    seasonalCore: 'Core',
    pogSetType: 'New Store',
    initialReplen: 'Initial',
  },
  {
    id: 'pd-9',
    pogGroup: 'POG-3002',
    event: 'SEA07',
    dc: '5_Los Angeles',
    po: '40660203',
    vendor: 'IN THE FLOW DESIGN LIMITED',
    sku: '10808178',
    inStoreDate: ymd('2026-03-27'),
    setDate: ymd('2026-03-27'),
    seasonalCore: 'Core',
    pogSetType: 'New Store',
    initialReplen: 'Initial',
  },
  {
    id: 'pd-10',
    pogGroup: 'POG-3002',
    event: 'SEA07',
    dc: '5_Los Angeles',
    po: '40660203',
    vendor: 'IN THE FLOW DESIGN LIMITED',
    sku: '10808180',
    inStoreDate: ymd('2026-03-27'),
    setDate: ymd('2026-03-27'),
    seasonalCore: 'Core',
    pogSetType: 'New Store',
    initialReplen: 'Initial',
  },
  {
    id: 'pd-11',
    pogGroup: 'POG-3002',
    event: 'SEA07',
    dc: '5_Los Angeles',
    po: '40660203',
    vendor: 'IN THE FLOW DESIGN LIMITED',
    sku: '10808181',
    inStoreDate: ymd('2026-03-27'),
    setDate: ymd('2026-03-27'),
    seasonalCore: 'Core',
    pogSetType: 'New Store',
    initialReplen: 'Initial',
  },
  {
    id: 'pd-12',
    pogGroup: 'POG-3002',
    event: 'SEA07',
    dc: '5_Los Angeles',
    po: '40660203',
    vendor: 'IN THE FLOW DESIGN LIMITED',
    sku: '10808184',
    inStoreDate: ymd('2026-03-27'),
    setDate: ymd('2026-03-27'),
    seasonalCore: 'Core',
    pogSetType: 'New Store',
    initialReplen: 'Initial',
  },
  {
    id: 'pd-13',
    pogGroup: 'POG-3002',
    event: 'SEA07',
    dc: '5_Los Angeles',
    po: '40660203',
    vendor: 'IN THE FLOW DESIGN LIMITED',
    sku: '10808245',
    inStoreDate: ymd('2026-03-27'),
    setDate: ymd('2026-03-27'),
    seasonalCore: 'Core',
    pogSetType: 'New Store',
    initialReplen: 'Initial',
  },
]

const FILLER_VENDORS = [
  'BEAU MONDE HONG KONG COMPANY LIMITED',
  'CASA COLLECTIVE LIMITED',
  'IN THE FLOW DESIGN LIMITED',
  'NORTHFIELD TRADING CO LIMITED',
  'GOLDEN HARVEST IMPORTS LIMITED',
  'BRIGHT PATH MANUFACTURING LIMITED',
]

const FILLER_DETAIL_ROW_COUNT = 45

function buildFillerDetailRow(index: number): PogDetailRow {
  const id = `pd-filler-${index}`
  const seed = hashString(id)

  const pogGroup =
    POG_GROUP_OPTIONS[
      Math.floor(normalizedFromHash(seed, 'pg') * POG_GROUP_OPTIONS.length)
    ]
  const event =
    EVENT_OPTIONS[
      Math.floor(normalizedFromHash(seed, 'ev') * EVENT_OPTIONS.length)
    ]
  const dc =
    DC_OPTIONS[Math.floor(normalizedFromHash(seed, 'dc') * DC_OPTIONS.length)]
  const vendor =
    FILLER_VENDORS[
      Math.floor(normalizedFromHash(seed, 'vendor') * FILLER_VENDORS.length)
    ]
  const seasonalCore =
    SEASONAL_CORE_OPTIONS[
      Math.floor(normalizedFromHash(seed, 'sc') * SEASONAL_CORE_OPTIONS.length)
    ]
  const pogSetType =
    POG_SET_TYPE_OPTIONS[
      Math.floor(normalizedFromHash(seed, 'pst') * POG_SET_TYPE_OPTIONS.length)
    ]
  const initialReplen =
    INITIAL_REPLEN_OPTIONS[
      Math.floor(normalizedFromHash(seed, 'ir') * INITIAL_REPLEN_OPTIONS.length)
    ]

  const setYear = 2026
  const setMonth = 1 + Math.floor(normalizedFromHash(seed, 'setMonth') * 12)
  const setDay = 1 + Math.floor(normalizedFromHash(seed, 'setDay') * 27)
  const setDate = new Date(setYear, setMonth - 1, setDay)
  const leadDays = Math.floor(normalizedFromHash(seed, 'lead') * 14) // 0-13 days early
  const inStoreDate = new Date(setDate)
  inStoreDate.setDate(inStoreDate.getDate() - leadDays)

  return {
    id,
    pogGroup,
    event,
    dc,
    po: String(40000000 + (hashString(id + 'po') % 9999999)),
    vendor,
    sku: String(10000000 + (hashString(id + 'sku') % 89999999)),
    inStoreDate,
    setDate,
    seasonalCore,
    pogSetType,
    initialReplen,
  }
}

const FILLER_DETAIL_ROWS: PogDetailRow[] = Array.from(
  { length: FILLER_DETAIL_ROW_COUNT },
  (_, i) => buildFillerDetailRow(i)
)

export const ALL_DETAIL_ROWS: PogDetailRow[] = [
  ...ANCHOR_DETAIL_ROWS,
  ...FILLER_DETAIL_ROWS,
]

export const PO_OPTIONS: string[] = Array.from(
  new Set(ALL_DETAIL_ROWS.map(r => r.po))
).sort()
export const SKU_OPTIONS: string[] = Array.from(
  new Set(ALL_DETAIL_ROWS.map(r => r.sku))
).sort()
export const VENDOR_OPTIONS: string[] = Array.from(
  new Set(ALL_DETAIL_ROWS.map(r => r.vendor))
).sort()

export function buildPogDetailRows(filters: PogFilterState): PogDetailRow[] {
  const cosmeticPairs = cosmeticFilterPairs(filters)
  const shrink = cosmeticPairs.reduce(
    (acc, [name, value]) => acc * shrinkForFilter(name, value),
    1
  )

  const rangeStart = filters.pogSetDateStart
    ? ymd(filters.pogSetDateStart)
    : null
  const rangeEnd = filters.pogSetDateEnd ? ymd(filters.pogSetDateEnd) : null

  const filtered = ALL_DETAIL_ROWS.filter(row => {
    if (filters.pogGroup && row.pogGroup !== filters.pogGroup) return false
    if (filters.event && row.event !== filters.event) return false
    if (filters.dc && row.dc !== filters.dc) return false
    if (filters.po && row.po !== filters.po) return false
    if (filters.sku && row.sku !== filters.sku) return false
    if (filters.vendor && row.vendor !== filters.vendor) return false
    if (filters.seasonalCore && row.seasonalCore !== filters.seasonalCore)
      return false
    if (filters.pogSetType && row.pogSetType !== filters.pogSetType)
      return false
    if (filters.initialReplen && row.initialReplen !== filters.initialReplen)
      return false
    if (rangeStart && row.setDate < rangeStart) return false
    if (rangeEnd && row.setDate > rangeEnd) return false
    return true
  })

  if (shrink === 1) return filtered

  // Cosmetic (department/class/subclass/season/snapshot-date) filters can't
  // be matched against these static rows (they're backed by live API ids),
  // so — same convention as the projection tab — they nudge the day
  // differential by a small deterministic amount instead of changing the
  // row set.
  return filtered.map(row => {
    const shrunkDiff = Math.max(0, Math.round(daysDiffFor(row) * shrink))
    const setDate = new Date(row.inStoreDate)
    setDate.setDate(setDate.getDate() + shrunkDiff)
    return { ...row, setDate }
  })
}
